import { useState } from "react";
import { IUserType } from "common/src/types";
import { useTranslation } from "react-i18next";
import { Box, Typography, TextField, Avatar, CircularProgress, Divider, InputAdornment, Paper, Chip, Tab, Tabs } from "@mui/material";
import { Add as AddIcon, Email as EmailIcon, Search as SearchIcon, Person as PersonIcon, CheckCircle as CheckIcon, Smartphone as SmartphoneIcon, Assignment as AssignmentIcon, PersonOutline as PersonOutlineIcon } from "@mui/icons-material";

interface PassengerSelectorProps {
    loading?: boolean;
    passengers?: IUserType[];
    selected?: IUserType | null;
    onSelect: (passenger: IUserType | null) => void;
}

export const PassengerSelector = ({
    onSelect,
    selected,
    loading = false,
    passengers = [],
}: PassengerSelectorProps) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const [tabValue, setTabValue] = useState(0);

    const getUserId = (user: IUserType): string => {
        return user?.uid || user?.id || user?.email || '';
    };

    const handleSelectPassenger = (person: IUserType) => {
        const personId = getUserId(person);
        const selectedId = selected ? getUserId(selected) : null;

        if (selectedId === personId && personId !== '') {
            onSelect(null);
            return;
        }

        onSelect(person);
    };

    const filtered = searchTerm.trim()
        ? passengers.filter((user) => {
            const fullName = `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();
            const term = searchTerm.toLowerCase().trim();
            return fullName.includes(term) ||
                user.email?.toLowerCase().includes(term) ||
                user.mobile?.includes(term) ||
                user.verifyId?.includes(term);
        })
        : passengers;

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);

        onSelect(null);
    };

    const formatCpf = (value: string) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
        if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
        return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
    };

    const formatPhone = (value: string) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 2) return numbers;
        if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    };

    if (loading) {
        return (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress size={50} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper"
            }}
        >
            <Box
                sx={{
                    p: 2,
                    borderBottom: 1,
                    display: "flex",
                    alignItems: "center",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    justifyContent: "space-between",
                }}
            >
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                    {t("ADM_PASSENGER")}
                </Typography>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: "background.paper" }}>
                <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
                    <Tab
                        iconPosition="start"
                        icon={<PersonIcon />}
                        label={t("ADM_SELECT_FROM_LIST")}
                    />
                    <Tab
                        icon={<AddIcon />}
                        iconPosition="start"
                        label={t("ADM_FILL_MANUALLY")}
                    />
                </Tabs>
            </Box>

            {tabValue === 0 && (
                <>
                    {selected && (
                        <>
                            <Paper
                                elevation={3}
                                sx={{
                                    p: 2,
                                    m: 2,
                                    flexShrink: 0,
                                    borderRadius: 4,
                                    overflow: "hidden",
                                    border: "4px solid",
                                    position: "relative",
                                    borderColor: "primary.main",
                                    bgcolor: "background.paper",
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Avatar
                                        src={selected.profile_image}
                                        sx={{ width: 56, height: 56 }}
                                    >
                                        <PersonIcon fontSize="large" />
                                    </Avatar>

                                    <Box flex={1} minWidth={0}>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{
                                                overflow: "hidden",
                                                whiteSpace: "nowrap",
                                                textOverflow: "ellipsis",
                                                color: "text.primary",
                                            }}
                                        >
                                            {selected.firstName || ""} {selected.lastName || ""}
                                        </Typography>

                                    </Box>

                                    <Chip
                                        size="small"
                                        color="primary"
                                        label={t("ADM_SELECTED")}
                                        icon={<CheckIcon sx={{ fontSize: 16 }} />}
                                        sx={{ fontWeight: "bold", flexShrink: 0 }}
                                    />
                                </Box>
                            </Paper>

                            <Divider />
                        </>
                    )}

                    <Box sx={{ p: 2, flexShrink: 0, bgcolor: "background.paper" }}>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            placeholder={t("ADM_SEARCH_PASSENGER_BY_NAME")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "text.secondary" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    <Divider />

                    <Box
                        sx={{
                            px: 1,
                            flex: 1,
                            minHeight: 0,
                            overflow: "auto",
                            position: "relative",

                        }}
                    >
                        {passengers.length === 0 ? (
                            <Typography align="center" color="text.secondary" py={3} variant="body2">
                                {t("ADM_NO_PASSENGERS")}
                            </Typography>
                        ) : filtered.length === 0 ? (
                            <Typography align="center" color="text.secondary" py={3} variant="body2">
                                {searchTerm ? t("ADM_NO_PASSENGERS_FOUND") : t("ADM_NO_PASSENGERS_AVAILABLE")}
                            </Typography>
                        ) : (
                            <Box
                                sx={{
                                    py: 2,
                                    flex: 1,
                                    gap: 2,
                                    display: "flex",
                                    overflow: "auto",
                                    flexDirection: "column",
                                }}
                            >
                                {filtered.map((person, index) => {
                                    const personId = getUserId(person);
                                    const selectedId = selected ? getUserId(selected) : null;
                                    const isSelected = selectedId === personId && personId !== '';

                                    return (
                                        <Box
                                            key={`passenger-${personId || index}`}
                                            onClick={() => handleSelectPassenger(person)}
                                            sx={{
                                                p: 2,
                                                gap: 2,
                                                minWidth: 0,
                                                width: "100%",
                                                borderRadius: 2,
                                                display: "flex",
                                                cursor: "pointer",
                                                border: "2px solid",
                                                alignItems: "center",
                                                transition: "all 0.2s ease",
                                                borderColor: isSelected ? "primary.main" : "transparent",
                                                bgcolor: isSelected ? "primary.light" : "background.paper",
                                                "&:hover": {
                                                    borderColor: isSelected ? "primary.main" : "divider",
                                                    bgcolor: isSelected ? "primary.light" : "action.hover",
                                                },
                                            }}
                                        >
                                            <Box sx={{ minWidth: 40, flexShrink: 0, position: "relative" }}>
                                                <Avatar
                                                    src={person.profile_image}
                                                    sx={{ width: 40, height: 40 }}
                                                >
                                                    <PersonIcon />
                                                </Avatar>
                                                {isSelected && (
                                                    <Box
                                                        sx={{
                                                            width: 18,
                                                            right: -4,
                                                            height: 18,
                                                            bottom: -4,
                                                            display: "flex",
                                                            borderRadius: "50%",
                                                            position: "absolute",
                                                            alignItems: "center",
                                                            bgcolor: "primary.main",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <CheckIcon sx={{ fontSize: 12, color: "white" }} />
                                                    </Box>
                                                )}
                                            </Box>

                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    overflow: "hidden"
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={isSelected ? 700 : 400}
                                                    color={isSelected ? "primary.contrastText" : "text.primary"}
                                                >
                                                    {person.firstName || ""} {person.lastName || ""}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color={isSelected ? "primary.contrastText" : "text.secondary"}
                                                    sx={{
                                                        display: "block",
                                                        overflow: "hidden",
                                                        whiteSpace: "nowrap",
                                                        textOverflow: "ellipsis",
                                                        opacity: isSelected ? 0.9 : 1,
                                                    }}
                                                >
                                                    {person.email || ""}
                                                    {person.mobile && ` • ${person.mobile}`}
                                                    {person.verifyId && ` • ${t("ADM_CPF_LABEL")} ${formatCpf(person.verifyId)}`}
                                                </Typography>
                                            </Box>

                                            {isSelected && (
                                                <CheckIcon color="primary" fontSize="medium" sx={{ flexShrink: 0 }} />
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </>
            )}

            {tabValue === 1 && (
                <Box sx={{ p: 3, overflow: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 4, bgcolor: "background.paper" }}>
                    <TextField
                        fullWidth
                        label={t("ADM_FIRST_NAME")}
                        value={selected?.firstName || ""}
                        placeholder={t("ADM_FIRST_NAME_PLACEHOLDER")}
                        onChange={(e) => {
                            const value = e.target.value;
                            onSelect({ ...selected, firstName: value } as IUserType);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PersonOutlineIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                bgcolor: "background.default",
                            }
                        }}
                    />

                    <TextField
                        fullWidth
                        label={t("ADM_LAST_NAME")}
                        value={selected?.lastName || ""}
                        placeholder={t("ADM_LAST_NAME_PLACEHOLDER")}
                        onChange={(e) => {
                            const value = e.target.value;
                            onSelect({ ...selected, lastName: value } as IUserType);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PersonOutlineIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                bgcolor: "background.default",
                            }
                        }}
                    />

                    <TextField
                        fullWidth
                        type="email"
                        label={t("ADM_EMAIL")}
                        value={selected?.email || ""}
                        placeholder={t("ADM_EMAIL_PLACEHOLDER")}
                        onChange={(e) => {
                            const value = e.target.value;
                            onSelect({ ...selected, email: value } as IUserType);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                bgcolor: "background.default",
                            }
                        }}
                    />

                    <TextField
                        fullWidth
                        label={t("ADM_MOBILE")}
                        placeholder={t("ADM_MOBILE_PLACEHOLDER")}
                        value={formatPhone(selected?.mobile || "")}
                        onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, '');
                            onSelect({ ...selected, mobile: raw } as IUserType);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SmartphoneIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                bgcolor: "background.default",
                            }
                        }}
                    />

                    <TextField
                        fullWidth
                        label={t("ADM_CPF")}
                        placeholder={t("ADM_CPF_PLACEHOLDER")}
                        value={formatCpf(selected?.verifyId || "")}
                        onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, '');
                            onSelect({ ...selected, verifyId: raw } as IUserType);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AssignmentIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                bgcolor: "background.default",
                            }
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};